<svelte:options tag={null} />
<script lang="typescript">
  import { 
    onMount,
    onDestroy,
    get_current_component 
  } from "svelte/internal";
  import '@everymatrix/retail-modal';

  import type { 
    OmPlayerLimits, 
    OmSportsLimits, 
    Sport 
  } from "./types/SportLimit.types";

  export let src: string;
  export let playerid: string;
  export let currency: string = 'EUR';
  export let edit: any;
  let authHeaders: HeadersInit;
  let isLoading: boolean;

  let loadingLimits = [];
  let loadingSports = [];
  
  // API Data
  let userLimits;
  let paymentLimits;

  let allSports: Sport[];
  let sportsLimits: OmPlayerLimits;
  let sportsError: string = null;
  // Possible states: init | failed | null (succeeded)
  let addLimitState: string = null;

  const thisComponent = get_current_component();

  const dispatchWcEvent = (name, detail) => {
    thisComponent.dispatchEvent(
      new CustomEvent(name, {
        detail,
        composed: true, // propagate across the shadow DOM
      })
    );
  };
  const getV2Url = (src: string) => {
    const url = new URL('/v2/player/', src)
    return url.href;
  }

  const fetchUserLimits = async (src: string, headers) => {
    try {
      
      const res = await fetch(src, { headers: { ...headers } });
      const data = await res.json();

      res.ok ? userLimits = {...data} : null;

    } catch (err) {
      throw new Error(`Api failed with: ${err}`);
    }
  };

  const fetchPaymentLimits = async (src: string, headers) => {
    try {
      const res = await fetch(src, { headers: { ...headers } });
      const data = await res.json();

      res.ok ? paymentLimits = [...data.items] : null;

    } catch (err) {
      throw new Error(`Api failed with: ${err}`);
    }
  };

  // Sports Limits
  const getAllSports = async (src:string) => {
    try {
      const res = await fetch(src);
      const { sports }:{sports: Sport[]} = await res.json();
      if(res.ok){
        return sports;
      }else{
        throw new Error("Failed to fetch List of sports");
      }
    } catch (error) {
      console.error(error)
    }
  }

  const fetchSportsLimits = async (src: string, playerid:string, headers: HeadersInit) => {
    try {
      isLoading = true;
      allSports = await getAllSports(`${src}sports`);
      const res = await fetch(`${src}${playerid}/limits/sport`, { headers })
      const data:OmPlayerLimits = await res.json().catch(err => console.log(err));
      
      if(res.ok){
        sportsLimits = {...data};
      } 
    } catch (error) {
      console.error(error)
    } finally {
      isLoading = false;
    }
  }

  const isLimitLoading = (limit: string, arr: string[]) => arr.includes( limit ) 

  const handleDeleteLimit = async (payload) => {
    try {
      loadingLimits = [...loadingLimits, payload.limit];
      const res = await fetch(`${src}${playerid}/${payload.endpoint}`, {
        method: "DELETE",
        headers: { ...authHeaders }
      });

      if (res.ok) {
        fetchPaymentLimits(`${src}${playerid}/paymentLimits`, { ...authHeaders });
        fetchUserLimits(`${getV2Url(src)}${playerid}/limits`, { ...authHeaders });
        dispatchWcEvent('change', '');
      }
    } catch (err) {
      throw new Error(`Api failed with: ${err}`);
    } finally {
      loadingLimits = [...loadingLimits.filter( limit => limit !== payload.limit )];
    }
  };

  const handleSportsLimitEdit = async (form: OmPlayerLimits, sportId) => {
    try {
      isLoading = true;
      const res = await fetch(`${src}${playerid}/limits/sport`, {
        method:"PUT",
        headers: {...authHeaders},
        body: JSON.stringify(form)
      })
      const { message } = await res.json();
      if(res.ok && message === 'OK') {
        fetchSportsLimits(src, playerid, {...authHeaders});
      } else {
        throw new Error("Failed to update current sports limits");
      }
    } catch (error) {
      sportsError = error
    } finally {
      isLoading = false;
      loadingSports = [...loadingSports.filter(id => {id !== sportId;})];
    }
  }

  const handleEvents = (e) => {
    // Set Auth Headers
    const {type, payload} = e.data;
    switch(type){
      case 'SetAuthHeaders':
        JSON.stringify(authHeaders) !== JSON.stringify(payload) && 
        (authHeaders = { ...payload })
        break;
      
      case 'LimitSavedSuccesfully':
        fetchUserLimits(`${getV2Url(src)}${playerid}/limits`, { ...authHeaders }) &&
        fetchPaymentLimits(`${src}${playerid}/paymentLimits`, { ...authHeaders }) &&
        fetchSportsLimits(src, playerid, {...authHeaders});
        addLimitState = null;
        break;

      case 'LimitSavedInit':
        addLimitState = 'init';
        break;
      case 'LimitSavedFailed':
        addLimitState = 'failed';
        break;
      default:
        break;
    }
  };

  onMount(() => {
    window.postMessage({ type: "GetAuthHeaders" }, "*");
    window.addEventListener("message", handleEvents);
  });

  onDestroy(() => {
    window.removeEventListener("message", handleEvents);
  });

  // Initialize Data
  $: src && 
    authHeaders && 
    playerid && 
    fetchPaymentLimits(`${src}${playerid}/paymentLimits`, { ...authHeaders }) &&
    fetchUserLimits(`${getV2Url(src)}${playerid}/limits`, { ...authHeaders }) &&
    fetchSportsLimits(src, playerid, {...authHeaders});

  $: isLoading;
</script>


{#if sportsLimits}
    <h3 class="title">
      <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <path d="M5 4a1 1 0 00-2 0v7.268a2 2 0 000 3.464V16a1 1 0 102 0v-1.268a2 2 0 000-3.464V4zM11 4a1 1 0 10-2 0v1.268a2 2 0 000 3.464V16a1 1 0 102 0V8.732a2 2 0 000-3.464V4zM16 3a1 1 0 011 1v7.268a2 2 0 010 3.464V16a1 1 0 11-2 0v-1.268a2 2 0 010-3.464V4a1 1 0 011-1z"></path>
      </svg>
      Sports Limits
    </h3>
    
    <sports-limits-active 
      on:edit|stopPropagation={({detail}) => { 
        handleSportsLimitEdit(detail.form, detail.sportLimit); loadingLimits = [...loadingLimits, 'sports']
        if (detail.sportLimit)
          loadingSports = [...loadingSports, detail.sportLimit]        
      }} 
      limits={sportsLimits} 
      error={sportsError} 
      sports={allSports} 
      isedit={edit}
      isloading={isLimitLoading('sports', [...loadingLimits])}
      loadingsports={[...loadingSports]}
    />
  {:else if isLoading}
  <ul class="loader">
    <li></li>
    <li></li>
    <li></li>
    <li></li>
    <li></li>
  </ul>
{:else if sportsLimits === null}
  <p style="margin-top: 20px; color: red; font-size: 16px;">
    No active sports limits
  </p>
{:else}
  <p style="margin-top: 20px; color: red; font-size: 16px;">
    Sports Limits API failed
  </p>
{/if}
<!-- Check Payment Limit API -->
{#if paymentLimits === null}
  <p style="margin-top: 20px; color: red; font-size: 16px;">
    Payment Limits API failed
  </p>
{/if}

<!-- Check Gambling Limit API -->
{#if userLimits === null}
  <p style="margin-top: 20px; color: red; font-size: 16px;">
    Gambling Limits Api Failed
  </p>
{/if}

{#if paymentLimits !== undefined && paymentLimits.length > 0}
  <!-- Payment User Limits -->
  <h3 class="title" style="margin-top: 0;">
    <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
      <path d="M5 4a1 1 0 00-2 0v7.268a2 2 0 000 3.464V16a1 1 0 102 0v-1.268a2 2 0 000-3.464V4zM11 4a1 1 0 10-2 0v1.268a2 2 0 000 3.464V16a1 1 0 102 0V8.732a2 2 0 000-3.464V4zM16 3a1 1 0 011 1v7.268a2 2 0 010 3.464V16a1 1 0 11-2 0v-1.268a2 2 0 010-3.464V4a1 1 0 011-1z"></path>
    </svg>
    Payment User Limits
  </h3>
  <!-- Payment Limits -->
  <payment-limit-active
    on:delete = {(e) => handleDeleteLimit(e.detail)}
    isloading={isLimitLoading('payment', [...loadingLimits])}
    limits={paymentLimits}
    isEdit={edit}
  />
{:else if (paymentLimits === undefined)}
  <!-- CARD LOADER-->
  <ul class="loader">
    <li></li>
    <li></li>
    <li></li>
    <li></li>
    <li></li>
  </ul>
{/if}

{#if userLimits}
  <div class="activeLimits">
    <!-- Gambling User Limits -->
    <h3 class="title">
      <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <path d="M5 4a1 1 0 00-2 0v7.268a2 2 0 000 3.464V16a1 1 0 102 0v-1.268a2 2 0 000-3.464V4zM11 4a1 1 0 10-2 0v1.268a2 2 0 000 3.464V16a1 1 0 102 0V8.732a2 2 0 000-3.464V4zM16 3a1 1 0 011 1v7.268a2 2 0 010 3.464V16a1 1 0 11-2 0v-1.268a2 2 0 010-3.464V4a1 1 0 011-1z"></path>
      </svg>
      Gambling User Limits
    </h3>
    <!-- Deposit Limit -->
    <deposit-limit-active
      on:delete = {(e) => handleDeleteLimit(e.detail)}
      isloading={isLimitLoading('deposit', [...loadingLimits])}
      limits={userLimits.depositLimits}
      currency={currency}
      isedit={edit}
    />
    <!-- Cumulative Sessions Limit -->
    <cumulative-limit-active
      on:delete = {(e) => handleDeleteLimit(e.detail)}
      isloading={isLimitLoading('cumulative', [...loadingLimits])}
      limits={userLimits.cumulativeSessionLimits}
      isedit={edit}
    />
    <!-- Wagering Limit -->
    <wagering-limit-active
      on:delete = {(e) => handleDeleteLimit(e.detail)}
      limits={userLimits.wageringLimits}
      currency={currency}
      isloading={isLimitLoading('wagering', [...loadingLimits])}
      isedit={edit}
    />
    <!-- Loss Limit -->
    <loss-limit-active
      on:delete = {(e) => handleDeleteLimit(e.detail)}
      limits={userLimits.lossLimits}
      currency={currency}
      isloading={isLimitLoading('loss', [...loadingLimits])}
      isedit={edit}
    />
    <!-- Session Limit -->
    <session-limit-active
    on:delete = {(e) => handleDeleteLimit(e.detail)}
      limit={userLimits.sessionLimit}
      isloading={isLimitLoading('session', [...loadingLimits])}
      isedit={edit}
    />
    <!-- Max Stake -->
    <maxstake-limit-active
      on:delete = {(e) => handleDeleteLimit(e.detail)}
      limit={userLimits.maxStake}
      isloading={isLimitLoading('maxstake', [...loadingLimits])}
      currency={currency}
      isedit={edit}
    />
    <!-- Self Exclussion && Cool Off -->
    <exclusion-limit-active
      on:delete = {(e) => handleDeleteLimit(e.detail)}
      limit={userLimits.selfExclusion}
      isloading={isLimitLoading('selfexclusion', [...loadingLimits])}
      isedit={edit}
    />
    {#if addLimitState === 'init'}
      <div class="sk-chase">
        <div class="sk-chase-dot"></div>
        <div class="sk-chase-dot"></div>
        <div class="sk-chase-dot"></div>
        <div class="sk-chase-dot"></div>
        <div class="sk-chase-dot"></div>
        <div class="sk-chase-dot"></div>
      </div>
    {:else if addLimitState === 'failed'}
      <p style="margin-top: 20px; color: red; font-size: 16px;">
        Failed to add new limit
      </p>
    {/if}
   
  </div>
{:else}
  <!-- CARD LOADER-->
  <ul class="loader">
    <li></li>
    <li></li>
    <li></li>
    <li></li>
    <li></li>
  </ul>
{/if}


<style lang="scss">
  *,
  *::before,
  *::after {
    margin: 0;
    padding: 0;
    list-style: none;
    text-decoration: none;
    outline: none;
    box-sizing: border-box;
    font-family: "Helvetica Neue", "Helvetica", sans-serif;
  }
  
  .title {
    display: flex;
    align-items: center;
    margin: 20px 0; 
    font-size: 16px;
    svg { 
      width: 20px; 
      margin-right: 5px;
      fill: #e09e17;
    }
  }

  .activeLimits {
    margin-top: 20px;
  }

  // Content Loader
  .loader {
    margin-top: 10px;
    li {
      position: relative;
      height: 18px;
      margin-bottom: 10px;
      animation-duration: 1.25s;
      animation-fill-mode: forwards;
      animation-iteration-count: infinite;
      animation-name: shimmer;
      animation-timing-function: linear;
      background: #F6F6F6;
      background: linear-gradient(to right, #F6F6F6 8%, #F0F0F0 18%, #f5f5f5 33%);
      background-size: 800px 104px;
      &:last-child { margin-bottom: 0; }
    }
  }
  // Animation
  @keyframes shimmer{
    0% {
      background-position: -468px 0
    }
    100% {
      background-position: 468px 0
    }
  }
  .sk-chase {
    width: 20px;
    height: 20px;
    position: relative;
    left: 2px;
    top: 2px;
    animation: sk-chase 2.5s infinite linear both;
  }
  .sk-chase-dot {
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0; 
    animation: sk-chase-dot 2.0s infinite ease-in-out both; 
  }

  .sk-chase-dot:before {
    content: '';
    display: block;
    width: 25%;
    height: 25%;
    background-color: green;
    border-radius: 100%;
    animation: sk-chase-dot-before 2.0s infinite ease-in-out both; 
  }

  .sk-chase-dot:nth-child(1) { animation-delay: -1.1s; }
  .sk-chase-dot:nth-child(2) { animation-delay: -1.0s; }
  .sk-chase-dot:nth-child(3) { animation-delay: -0.9s; }
  .sk-chase-dot:nth-child(4) { animation-delay: -0.8s; }
  .sk-chase-dot:nth-child(5) { animation-delay: -0.7s; }
  .sk-chase-dot:nth-child(6) { animation-delay: -0.6s; }
  .sk-chase-dot:nth-child(1):before { animation-delay: -1.1s; }
  .sk-chase-dot:nth-child(2):before { animation-delay: -1.0s; }
  .sk-chase-dot:nth-child(3):before { animation-delay: -0.9s; }
  .sk-chase-dot:nth-child(4):before { animation-delay: -0.8s; }
  .sk-chase-dot:nth-child(5):before { animation-delay: -0.7s; }
  .sk-chase-dot:nth-child(6):before { animation-delay: -0.6s; }

  @keyframes sk-chase {
    100% { transform: rotate(360deg); } 
  }

  @keyframes sk-chase-dot {
    80%, 100% { transform: rotate(360deg); } 
  }

  @keyframes sk-chase-dot-before {
    50% {
      transform: scale(0.4); 
    } 100%, 0% {
      transform: scale(1.0); 
    } 
  }
</style>
